/**
 * StudoX Core — "SINGULARITY GATEWAY"  ///  Ground-up rebuild v6
 * ─────────────────────────────────────────────────────────────────────────────
 * Not an iteration — a new scene. The Core is now a gravitational gateway:
 *
 *   ① GLSL NEBULA — a fullscreen fbm-noise cloudscape (domain-warped, slowly
 *      breathing violet/indigo/cyan with rare gold veins). No sprite blobs.
 *   ② THE SINGULARITY — 24 000 GPU particles forming a differential-rotation
 *      accretion disk (inner white-gold → violet → outer cyan). During boot
 *      the particles start as scattered cosmic chaos and get PULLED into the
 *      disk (per-particle staggered formation wave driven by uForm).
 *      A pure-black event-horizon sphere occludes the disk behind it and a
 *      brilliant photon ring rims it — a real black-hole silhouette.
 *   ③ ECOSYSTEM HUBS — the 6 app-nodes orbit OUTSIDE the disk, each firing
 *      light-pulses down data-streams into the singularity, linked by a
 *      breathing energy net. The apps literally feed the Core.
 *   ④ CAMERA CHOREOGRAPHY — the boot is a single continuous shot:
 *      wide + high during chaos → dive during warp → settle into the hero
 *      framing on ignite. Mouse parallax + film roll once idle.
 *   ⑤ Twinkling galaxy starfield, shooting stars, scrolling holo-grid floor.
 *
 * Public API unchanged: <ThreeBackground phase warp ignitePulse />
 */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass }      from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

const HUBS = [
  { col: 0x8b5cf6, ph: 0.00 }, // violet
  { col: 0x22d3ee, ph: 1.05 }, // cyan
  { col: 0xfbbf24, ph: 2.09 }, // gold
  { col: 0xec4899, ph: 3.14 }, // magenta
  { col: 0x10b981, ph: 4.19 }, // teal
  { col: 0xf97316, ph: 5.24 }, // orange
];

/* ── shared soft-glow / ring / grid canvas textures ─────────────────────── */
function makeGlowTex(res = 256) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = res;
  const cx = cv.getContext("2d"), h = res / 2;
  const g = cx.createRadialGradient(h, h, 0, h, h, h);
  g.addColorStop(0.00, "rgba(255,255,255,1)");
  g.addColorStop(0.12, "rgba(255,255,255,0.85)");
  g.addColorStop(0.35, "rgba(255,255,255,0.32)");
  g.addColorStop(0.65, "rgba(255,255,255,0.08)");
  g.addColorStop(1.00, "rgba(255,255,255,0)");
  cx.fillStyle = g; cx.fillRect(0, 0, res, res);
  return new THREE.CanvasTexture(cv);
}
function makeRingTex(res = 256) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = res;
  const cx = cv.getContext("2d"), h = res / 2;
  cx.strokeStyle = "rgba(255,255,255,1)";
  cx.lineWidth = res * 0.055;
  cx.filter = "blur(4px)";
  cx.beginPath(); cx.arc(h, h, h * 0.66, 0, Math.PI * 2); cx.stroke();
  cx.filter = "blur(1px)";
  cx.lineWidth = res * 0.016;
  cx.beginPath(); cx.arc(h, h, h * 0.66, 0, Math.PI * 2); cx.stroke();
  return new THREE.CanvasTexture(cv);
}
function makeGridTex(res = 512) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = res;
  const cx = cv.getContext("2d");
  cx.strokeStyle = "rgba(139,92,246,0.55)"; cx.lineWidth = 1.4;
  const step = res / 16;
  for (let i = 0; i <= 16; i++) {
    cx.beginPath(); cx.moveTo(i * step, 0); cx.lineTo(i * step, res); cx.stroke();
    cx.beginPath(); cx.moveTo(0, i * step); cx.lineTo(res, i * step); cx.stroke();
  }
  cx.strokeStyle = "rgba(34,211,238,0.7)"; cx.lineWidth = 2.4;
  for (let i = 0; i <= 4; i++) {
    cx.beginPath(); cx.moveTo(i * step * 4, 0); cx.lineTo(i * step * 4, res); cx.stroke();
    cx.beginPath(); cx.moveTo(0, i * step * 4); cx.lineTo(res, i * step * 4); cx.stroke();
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(6, 6);
  return tex;
}

/* ═══════════════════════════════════════════════════════════════════════════
   GLSL — nebula backdrop
   ═══════════════════════════════════════════════════════════════════════════ */
const NEBULA_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const NEBULA_FRAG = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uEnergy;
  uniform vec2  uRes;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x),
               mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.03; a *= 0.5; }
    return v;
  }

  void main() {
    /* noise coords in PLANE space (aspect-corrected to the huge sky quad) */
    vec2 uv = vUv * 2.0 - 1.0;
    uv.x *= 5.8;
    float t = uTime * 0.018;

    /* domain-warped fbm — living clouds, not static noise */
    vec2 q = vec2(fbm(uv * 1.4 + t), fbm(uv * 1.4 - t * 0.7 + 5.2));
    vec2 r = vec2(fbm(uv * 2.1 + q * 1.35 + t * 0.5 + 1.7),
                  fbm(uv * 2.1 + q * 1.35 - t * 0.4 + 8.3));
    float f = fbm(uv * 1.8 + r * 1.5);

    vec3 deep   = vec3(0.016, 0.006, 0.10);   /* abyss indigo   */
    vec3 violet = vec3(0.23,  0.09,  0.52);   /* nebula violet  */
    vec3 cyan   = vec3(0.02,  0.28,  0.42);   /* teal depths    */
    vec3 gold   = vec3(0.55,  0.33,  0.08);   /* rare gold vein */

    vec3 col = deep;
    col = mix(col, violet, smoothstep(0.32, 0.78, f) * 0.85);
    col = mix(col, cyan,   smoothstep(0.45, 0.95, q.y) * 0.5);
    col = mix(col, gold,   smoothstep(0.68, 0.92, r.x * f) * 0.55);
    col *= 0.85 + uEnergy * 0.5;

    /* vignette + centre pull in SCREEN space — perfect on 16:9, ultrawide
       5120px, portrait, everything. Never tied to the plane again. */
    vec2 sc = (gl_FragCoord.xy / uRes) * 2.0 - 1.0;
    float d = length(sc * vec2(0.9, 1.0));
    col *= smoothstep(1.75, 0.35, d) * 0.9 + 0.1;
    col *= 1.0 - smoothstep(0.55, 0.0, d) * 0.55;

    gl_FragColor = vec4(col, 1.0);
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   GLSL — 24k-particle accretion disk (chaos → formation via uForm)
   ═══════════════════════════════════════════════════════════════════════════ */
const DISK_VERT = /* glsl */`
  attribute float seed;
  uniform float uTime, uForm, uIgnite, uWarp;
  varying vec3 vCol;
  varying float vA;

  float h(float n) { return fract(sin(n) * 43758.5453); }

  void main() {
    float s1 = h(seed), s2 = h(seed + 1.7), s3 = h(seed + 3.1), s4 = h(seed + 5.3);

    /* CHAOS — a vast, slowly tumbling cloud */
    float cr  = 7.0 + s1 * 15.0;
    float cth = s2 * 6.2831 + uTime * 0.02;
    float cph = acos(2.0 * s3 - 1.0);
    vec3 chaos = vec3(cr * sin(cph) * cos(cth), cr * cos(cph) * 0.62, cr * sin(cph) * sin(cth));

    /* DISK — differential rotation, inner matter orbits far faster */
    float r    = 1.30 + pow(s1, 1.55) * 3.55;
    float spin = uTime * (1.05 / pow(r, 1.5)) * (1.0 + uWarp * 2.5 + uIgnite * 3.0);
    float ang  = s2 * 6.2831 + spin;
    float yy   = (s3 - 0.5) * 0.26 * pow(r / 4.8, 1.3);
    vec3 disk  = vec3(r * cos(ang), yy, r * sin(ang) * 0.985);

    /* staggered formation wave — matter falls in, particle by particle */
    float f = clamp(uForm * 1.7 - s4 * 0.7, 0.0, 1.0);
    f = f * f * (3.0 - 2.0 * f);
    vec3 pos = mix(chaos, disk, f);
    pos += normalize(pos + 0.001) * uIgnite * 0.6 * s4;   /* ignite blast puff */

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = (0.7 + s1 * 0.9) * (1.1 + uIgnite * 1.2) * (30.0 / -mv.z);

    /* radial colour ramp: white-gold core → violet body → cyan rim */
    float tR = clamp((r - 1.30) / 3.55, 0.0, 1.0);
    vec3 hot  = vec3(1.00, 0.90, 0.70);
    vec3 mid  = vec3(0.60, 0.34, 1.00);
    vec3 rim  = vec3(0.18, 0.82, 1.00);
    vec3 col  = mix(hot, mid, smoothstep(0.0, 0.5, tR));
    col       = mix(col, rim, smoothstep(0.5, 1.0, tR));

    /* chaos matter is cold and dim; formed matter glows — kept LOW because
       thousands of additive points + bloom overexpose fast */
    vCol = mix(col * 0.30 + vec3(0.08, 0.10, 0.28), col, f) * (0.38 + 0.30 * s2);
    vCol *= 1.0 + uIgnite * 1.4;
    vA = 0.16 + 0.24 * f;
  }
`;
const DISK_FRAG = /* glsl */`
  precision mediump float;
  varying vec3 vCol;
  varying float vA;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float a = smoothstep(0.5, 0.0, d);
    gl_FragColor = vec4(vCol, a * a * vA);
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   GLSL — twinkling starfield (per-star phase in the shader)
   ═══════════════════════════════════════════════════════════════════════════ */
const STAR_VERT = /* glsl */`
  attribute float seed;
  attribute vec3 sCol;
  uniform float uTime;
  varying vec3 vCol;
  varying float vTw;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float tw = 0.55 + 0.45 * sin(uTime * (0.6 + fract(seed) * 2.2) + seed * 12.9);
    vTw = tw;
    vCol = sCol;
    gl_PointSize = (1.1 + fract(seed * 7.31) * 2.4) * tw * (30.0 / -mv.z);
  }
`;
const STAR_FRAG = /* glsl */`
  precision mediump float;
  varying vec3 vCol;
  varying float vTw;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float a = smoothstep(0.5, 0.0, length(uv));
    gl_FragColor = vec4(vCol, a * a * vTw);
  }
`;

export default function ThreeBackground({ phase = 5, warp = false, ignitePulse = 0 }) {
  const mountRef  = useRef(null);
  const phaseRef  = useRef(phase);
  const warpRef   = useRef(warp);
  const igniteRef = useRef(0);
  const springRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const rawMouse  = useRef({ x: 0, y: 0 });

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { warpRef.current = warp; }, [warp]);
  useEffect(() => { if (ignitePulse) igniteRef.current = 1; }, [ignitePulse]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x040112, 1);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.85;
    mount.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 600);
    camera.position.set(0, 3.4, 21);
    camera.lookAt(0, 0, 0);

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.52, 0.55, 0.42);
    composer.addPass(bloomPass);

    const ADD = THREE.AdditiveBlending;
    const glowTex = makeGlowTex();
    const ringTex = makeRingTex();
    const gridTex = makeGridTex();

    /* ① NEBULA backdrop — parented to the CAMERA like a true skybox layer.
       It can never rotate/drift out of view (the ultrawide hard-edge bug):
       at distance 120 the frustum needs ~118 world-units of height — the
       1100×190 quad overfills every aspect ratio up to ~6:1 with margin.
       The vignette lives in screen space (uRes), so the grade is identical
       on 16:9, 21:9 and 32:9 ultrawides. */
    const nebulaMat = new THREE.ShaderMaterial({
      vertexShader: NEBULA_VERT, fragmentShader: NEBULA_FRAG,
      uniforms: {
        uTime: { value: 0 },
        uEnergy: { value: 0 },
        uRes: { value: new THREE.Vector2(window.innerWidth * renderer.getPixelRatio(), window.innerHeight * renderer.getPixelRatio()) },
      },
      depthWrite: false,
      depthTest: false,
    });
    const nebula = new THREE.Mesh(new THREE.PlaneGeometry(1100, 190), nebulaMat);
    nebula.position.set(0, 0, -120);
    nebula.renderOrder = -1;
    scene.add(camera);      // camera children render only if camera is in the scene
    camera.add(nebula);

    /* ② STARFIELD — twinkle computed in-shader */
    {
      const N = 3600;
      const pos = new Float32Array(N * 3), col = new Float32Array(N * 3), seed = new Float32Array(N);
      const pal = [[0.65, 0.55, 1.0], [0.45, 0.85, 1.0], [1.0, 0.85, 0.55], [0.9, 0.9, 1.0], [0.75, 0.5, 1.0]];
      for (let i = 0; i < N; i++) {
        const rr = 22 + Math.random() * 60, th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1);
        pos[i * 3]     = rr * Math.sin(ph) * Math.cos(th);
        pos[i * 3 + 1] = rr * Math.sin(ph) * Math.sin(th) * 0.7;
        pos[i * 3 + 2] = rr * Math.cos(ph) - 20;
        const p = pal[Math.floor(Math.random() * pal.length)], b = 0.25 + Math.random() * 0.6;
        col[i * 3] = p[0] * b; col[i * 3 + 1] = p[1] * b; col[i * 3 + 2] = p[2] * b;
        seed[i] = Math.random() * 100;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      g.setAttribute("sCol", new THREE.BufferAttribute(col, 3));
      g.setAttribute("seed", new THREE.BufferAttribute(seed, 1));
      var starMat = new THREE.ShaderMaterial({
        vertexShader: STAR_VERT, fragmentShader: STAR_FRAG,
        uniforms: { uTime: { value: 0 } },
        transparent: true, blending: ADD, depthWrite: false,
      });
      var stars = new THREE.Points(g, starMat);
      scene.add(stars);
    }

    /* ③ HOLO-GRID FLOOR — endless forward scroll */
    const grid = new THREE.Mesh(
      new THREE.PlaneGeometry(140, 140),
      new THREE.MeshBasicMaterial({ map: gridTex, color: 0x8b5cf6, transparent: true, opacity: 0.10, blending: ADD, depthWrite: false, side: THREE.DoubleSide })
    );
    grid.rotation.x = -Math.PI / 2 + 0.06;
    grid.position.set(0, -8.5, -10);
    scene.add(grid);

    /* ④ THE SINGULARITY ──────────────────────────────────────────────── */
    const nexus = new THREE.Object3D();
    nexus.rotation.x = 0.42;                 /* cinematic disk tilt */
    scene.add(nexus);

    /* accretion disk — 24 000 GPU particles */
    const P = 24000;
    const dSeed = new Float32Array(P);
    for (let i = 0; i < P; i++) dSeed[i] = Math.random() * 1000;
    const diskGeo = new THREE.BufferGeometry();
    diskGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(P * 3), 3)); // computed in shader
    diskGeo.setAttribute("seed", new THREE.BufferAttribute(dSeed, 1));
    diskGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);
    const diskMat = new THREE.ShaderMaterial({
      vertexShader: DISK_VERT, fragmentShader: DISK_FRAG,
      uniforms: { uTime: { value: 0 }, uForm: { value: 0 }, uIgnite: { value: 0 }, uWarp: { value: 0 } },
      transparent: true, blending: ADD, depthWrite: false,
    });
    const disk = new THREE.Points(diskGeo, diskMat);
    disk.frustumCulled = false;
    nexus.add(disk);

    /* event horizon — pure black occluder + photon ring + core glow */
    const horizon = new THREE.Mesh(
      new THREE.SphereGeometry(0.52, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0x000000 })
    );
    nexus.add(horizon);
    const photonRing = new THREE.Sprite(new THREE.SpriteMaterial({
      map: ringTex, color: 0xfff3d0, transparent: true, opacity: 0.9, blending: ADD, depthWrite: false,
    }));
    photonRing.scale.set(1.9, 1.9, 1);
    nexus.add(photonRing);
    const coreGlow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: glowTex, color: 0xc4b5fd, transparent: true, opacity: 0.14, blending: ADD, depthWrite: false,
    }));
    coreGlow.scale.set(3.0, 3.0, 1);
    nexus.add(coreGlow);

    /* ignite shock ring */
    const shockRing = new THREE.Sprite(new THREE.SpriteMaterial({
      map: ringTex, color: 0xfde68a, transparent: true, opacity: 0, blending: ADD, depthWrite: false,
    }));
    nexus.add(shockRing);

    /* two thin gateway rings framing the disk */
    const gateRings = [
      { r: 5.1, col: 0x8b5cf6, spd: 0.10, tilt: 0.5 },
      { r: 5.55, col: 0x22d3ee, spd: -0.07, tilt: -0.35 },
    ].map((cfg) => {
      const grp = new THREE.Object3D();
      grp.rotation.x = cfg.tilt;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(cfg.r, 0.012, 10, 140),
        new THREE.MeshBasicMaterial({ color: cfg.col, transparent: true, opacity: 0.5, blending: ADD, depthWrite: false })
      );
      ring.rotation.x = Math.PI / 2;
      grp.add(ring);
      nexus.add(grp);
      return { grp, spd: cfg.spd };
    });

    /* ⑤ ECOSYSTEM HUBS — orbit outside the disk, feed the singularity */
    const NR = 6.1;
    const hubs = HUBS.map((h, i) => {
      const angle0 = (i / HUBS.length) * Math.PI * 2;
      const node = new THREE.Mesh(
        new THREE.SphereGeometry(0.11, 14, 14),
        new THREE.MeshBasicMaterial({ color: h.col, blending: ADD })
      );
      const nodeGlow = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: h.col, transparent: true, opacity: 0.75, blending: ADD, depthWrite: false,
      }));
      nodeGlow.scale.set(0.66, 0.66, 1);
      node.add(nodeGlow);
      const halo = new THREE.Sprite(new THREE.SpriteMaterial({
        map: ringTex, color: h.col, transparent: true, opacity: 0.5, blending: ADD, depthWrite: false,
      }));
      halo.scale.set(0.95, 0.95, 1);
      node.add(halo);
      nexus.add(node);

      const streamGeo = new THREE.BufferGeometry();
      streamGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
      streamGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array([
        (h.col >> 16 & 255) / 255, (h.col >> 8 & 255) / 255, (h.col & 255) / 255, 0, 0, 0,
      ]), 3));
      const stream = new THREE.Line(streamGeo, new THREE.LineBasicMaterial({
        vertexColors: true, transparent: true, opacity: 0.45, blending: ADD, depthWrite: false,
      }));
      nexus.add(stream);

      const pulse = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: h.col, transparent: true, opacity: 0, blending: ADD, depthWrite: false,
      }));
      pulse.scale.set(0.3, 0.3, 1);
      nexus.add(pulse);

      return { node, stream, pulse, angle0, ph: h.ph, pulseT: h.ph / 6.28 };
    });

    /* hub energy net */
    const netGeo = new THREE.BufferGeometry();
    netGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(HUBS.length * 3), 3));
    const hubNet = new THREE.LineLoop(netGeo, new THREE.LineBasicMaterial({
      color: 0x8b5cf6, transparent: true, opacity: 0.15, blending: ADD, depthWrite: false,
    }));
    nexus.add(hubNet);

    /* shooting stars */
    const shoots = Array.from({ length: 3 }, (_, i) => {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glowTex, color: 0xcfe9ff, transparent: true, opacity: 0, blending: ADD, depthWrite: false,
      }));
      sp.scale.set(2.0, 0.055, 1);
      scene.add(sp);
      return { sp, from: new THREE.Vector3(), to: new THREE.Vector3(), t0: -1, dur: 1, next: 3 + i * 4 };
    });
    const launchShoot = (s, now) => {
      const x = (Math.random() - 0.5) * 30, y = 4 + Math.random() * 6, z = -12 - Math.random() * 10;
      s.from.set(x, y, z);
      s.to.set(x + 8 + Math.random() * 9, y - 5 - Math.random() * 3, z);
      s.t0 = now; s.dur = 0.8 + Math.random() * 0.5;
      s.next = now + s.dur + 4 + Math.random() * 8;
      s.sp.material.rotation = Math.atan2(s.to.y - s.from.y, s.to.x - s.from.x);
    };

    /* ── input & resize ─────────────────────────────────────────────── */
    const onMove = (e) => {
      rawMouse.current.x =  (e.clientX / window.innerWidth  - 0.5) * 2;
      rawMouse.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    const onTouch = (e) => {
      if (!e.touches[0]) return;
      rawMouse.current.x =  (e.touches[0].clientX / window.innerWidth  - 0.5) * 2;
      rawMouse.current.y = -(e.touches[0].clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: true });
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      composer.setSize(window.innerWidth, window.innerHeight);
      nebulaMat.uniforms.uRes.value.set(window.innerWidth * renderer.getPixelRatio(), window.innerHeight * renderer.getPixelRatio());
    };
    window.addEventListener("resize", onResize);

    /* ── the continuous shot ────────────────────────────────────────── */
    let raf, t = 0, warpT = 0, form = 0;
    const camPos = new THREE.Vector3(0, 3.4, 21);
    const camTarget = new THREE.Vector3();

    const animate = () => {
      raf = requestAnimationFrame(animate);
      t += 0.016;

      const ph = phaseRef.current;
      warpT += warpRef.current ? 0.05 : -0.05;
      warpT = Math.max(0, Math.min(1, warpT));
      const ig = igniteRef.current;
      if (ig > 0.001) igniteRef.current *= 0.94; else igniteRef.current = 0;

      /* formation: chaos → disk, driven by boot phase */
      const formTarget = ph >= 2 ? 1 : ph >= 1 ? 0.42 : 0.06;
      form += (formTarget - form) * 0.035;

      /* camera choreography — one continuous dolly through the boot */
      const rm = rawMouse.current, sp = springRef.current;
      if (ph <= 1)      camTarget.set(Math.sin(t * 0.06) * 1.4, 3.6, 21);
      else if (ph === 2) camTarget.set(0, 1.6, 14.5);
      else if (ph === 3) camTarget.set(0, 0.7, 12.2);
      else               camTarget.set(Math.sin(t * 0.05) * 0.9, 0.35 + Math.cos(t * 0.042) * 0.45, 12 + Math.sin(t * 0.03) * 0.55);
      camPos.lerp(camTarget, ph === 2 ? 0.055 : 0.035);
      camera.position.copy(camPos);
      camera.position.z -= ig * 1.3;                     /* ignite punch-in */
      camera.lookAt(0, 0, 0);
      camera.rotation.z += Math.sin(t * 0.047) * 0.013;  /* film roll */

      /* uniforms */
      nebulaMat.uniforms.uTime.value = t;
      nebulaMat.uniforms.uEnergy.value = form * 0.5 + warpT * 0.5 + ig * 0.8;
      starMat.uniforms.uTime.value = t;
      diskMat.uniforms.uTime.value = t;
      diskMat.uniforms.uForm.value = form;
      diskMat.uniforms.uWarp.value = warpT;
      diskMat.uniforms.uIgnite.value = ig;

      /* nexus parallax spring + slow precession */
      const driftX = Math.sin(t * 0.11) * 0.3, driftY = Math.cos(t * 0.09) * 0.18;
      const tx = (rm.x * 0.8 + driftX - sp.x) * 0.02, ty = (rm.y * 0.5 + driftY - sp.y) * 0.02;
      sp.vx = (sp.vx + tx) * 0.9; sp.vy = (sp.vy + ty) * 0.9;
      sp.x += sp.vx; sp.y += sp.vy;
      nexus.position.set(sp.x, sp.y, 0);
      nexus.rotation.y += 0.0012 * (1 + warpT * 3 + ig * 2);
      nexus.rotation.z = Math.sin(t * 0.07) * 0.03;

      /* photon ring & core glow breathe; flare on ignite */
      photonRing.material.opacity = (0.55 + Math.sin(t * 1.3) * 0.12) * form + ig * 0.8;
      photonRing.scale.setScalar(1.9 + Math.sin(t * 1.3) * 0.05 + ig * 1.2);
      coreGlow.material.opacity = 0.22 + form * 0.16 + ig * 0.5;
      coreGlow.scale.setScalar(3.6 + ig * 2.4);

      if (ig > 0.001) {
        shockRing.material.opacity = ig * 0.85;
        shockRing.scale.setScalar(0.4 + (1 - ig) * 13);
      } else shockRing.material.opacity = 0;

      gateRings.forEach(({ grp, spd }) => { grp.rotation.z += spd * 0.05 * (1 + warpT * 4 + ig * 5); });

      /* hubs orbit + streams + pulses + net */
      const netArr = hubNet.geometry.attributes.position.array;
      hubs.forEach((h, i) => {
        const a = h.angle0 + t * 0.085 * (1 + warpT * 2 + ig * 2.5);
        const y = Math.sin(t * 0.4 + h.ph) * 0.4;
        const nx = NR * Math.cos(a), nz = NR * Math.sin(a);
        h.node.position.set(nx, y, nz);
        h.node.visible = form > 0.5;

        h.pulseT += 0.006 * (1 + warpT * 3 + ig * 4);
        if (h.pulseT > 1) h.pulseT -= 1;
        const pt = h.pulseT;
        h.pulse.position.set(nx * (1 - pt), y * (1 - pt), nz * (1 - pt));
        h.pulse.material.opacity = Math.sin(pt * Math.PI) * 0.85 * form;

        const pa = h.stream.geometry.attributes.position;
        pa.array[0] = nx; pa.array[1] = y; pa.array[2] = nz;
        pa.array[3] = 0;  pa.array[4] = 0; pa.array[5] = 0;
        pa.needsUpdate = true;
        h.stream.material.opacity = 0.4 * form;

        netArr[i * 3] = nx; netArr[i * 3 + 1] = y; netArr[i * 3 + 2] = nz;
      });
      hubNet.geometry.attributes.position.needsUpdate = true;
      hubNet.material.opacity = (0.10 + Math.sin(t * 0.8) * 0.05 + warpT * 0.15 + ig * 0.3) * form;

      /* shooting stars */
      shoots.forEach((s) => {
        if (s.t0 < 0 && t > s.next) launchShoot(s, t);
        if (s.t0 >= 0) {
          const p = (t - s.t0) / s.dur;
          if (p >= 1) { s.sp.material.opacity = 0; s.t0 = -1; }
          else {
            s.sp.position.lerpVectors(s.from, s.to, p);
            s.sp.material.opacity = Math.sin(p * Math.PI) * 0.7;
          }
        }
      });

      /* stars slow drift; grid scroll */
      stars.rotation.y += 0.00012 * (1 + warpT * 14);
      grid.material.opacity = 0.08 + Math.sin(t * 0.6) * 0.02 + warpT * 0.05 + ig * 0.1;
      gridTex.offset.y = (t * 0.015 * (1 + warpT * 6)) % 1;

      bloomPass.strength = 0.85 + warpT * 0.5 + ig * 1.25;
      composer.render();
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("resize", onResize);
      glowTex.dispose(); ringTex.dispose(); gridTex.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose(); composer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: "fixed", inset: 0, zIndex: 0,
        opacity: phase >= 1 ? 1 : 0,
        transition: "opacity 1.6s ease",
      }}
    />
  );
}
